import Hls from "hls.js";

window.addEventListener("load", () => {
	/* =====================================================================
	   DISCLAIMER MODAL — br-blocks style
	===================================================================== */

	const overlay = document.createElement("div");
	overlay.className = "modal-overlay";
	document.body.appendChild(overlay);

	const modalContent = document.createElement("div");
	modalContent.className = "modal-content";
	document.body.appendChild(modalContent);

	modalContent.innerHTML = `
		<div class="modal-header">
			<div>
				<h3 class="modal-title">Disclaimers</h3>
			</div>
			<span class="br-modal-close" role="button" aria-label="Close">&times;</span>
		</div>
		<div class="scrollable-content wc-disclaimer-modal-body"></div>
	`;

	const modalBody    = modalContent.querySelector(".wc-disclaimer-modal-body");
	const closeBtn     = modalContent.querySelector(".br-modal-close");
	let   closingTimer = null;

	function openModal(contentEl) {
		modalBody.innerHTML = "";
		const clone = contentEl.cloneNode(true);
		clone.removeAttribute("hidden");
		clone.style.maxHeight = "";
		clone.style.overflow  = "";
		modalBody.appendChild(clone);

		clearTimeout(closingTimer);
		modalContent.classList.remove("is-closing");

		document.body.classList.add("modal-open");
		overlay.classList.add("is-visible");

		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				modalContent.classList.add("is-visible");
			});
		});
	}

	function closeModal() {
		overlay.classList.remove("is-visible");
		modalContent.classList.remove("is-visible");
		modalContent.classList.add("is-closing");

		closingTimer = setTimeout(() => {
			modalContent.classList.remove("is-closing");
			document.body.classList.remove("modal-open");
		}, 300);
	}

	overlay.addEventListener("click", closeModal);
	closeBtn.addEventListener("click", closeModal);

	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && modalContent.classList.contains("is-visible")) {
			closeModal();
		}
	});

	document.querySelectorAll(".wc-disclaimer-toggle").forEach((btn) => {
		const contentEl = btn.nextElementSibling;
		if (!contentEl) return;
		btn.addEventListener("click", () => openModal(contentEl));
	});

	/* =====================================================================
	   HELPERS
	===================================================================== */

	function extractAllSessions(title) {
		if (!title) return [];
		const matches = [...title.matchAll(/Session\s+(\d+)/gi)];
		return matches.map((m) => `Session ${m[1]}`);
	}

	function formatDateLabel(dateStr) {
		const parts = dateStr.split("/");
		if (parts.length !== 3) return dateStr;
		const [day, month, year] = parts;
		const d = new Date(`${year}-${month}-${day}`);
		return d
			.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
			.replace(".", "");
	}

	/* =====================================================================
	   PARSE SCHEDULE HTML -> SESSIONS
	===================================================================== */

	function parseScheduleHTML(html) {
		const parser = new DOMParser();
		const doc    = parser.parseFromString(html, "text/html");
		const table  = doc.querySelector(".views-table");
		if (!table) return { sessions: [], table: null };

		const rows     = [...table.querySelectorAll("tr")].filter((r) => r.querySelector("td"));
		const sessions = [];

		rows.forEach((row) => {
			const cols = row.querySelectorAll("td");
			if (cols.length < 2) return;

			const dateTd = cols[0];
			const infoTd = cols[1];

			const timeEl       = dateTd.querySelector("time");
			const datetimeISO  = timeEl?.getAttribute("datetime") || "";
			const dateTextCore = timeEl?.textContent.trim() || "";
			const extraText    = dateTd.textContent.replace(dateTextCore, "").trim();

			let dateDay  = "";
			let timeText = "";

			if (dateTextCore.includes(" - ")) {
				const parts = dateTextCore.split(" - ");
				dateDay  = parts[0].trim();
				timeText = parts[1].trim();
			} else {
				dateDay = dateTextCore.trim();
			}

			const dateDisplay = dateTextCore + (extraText ? " " + extraText : "");

			const firstDiv = infoTd.querySelector("div");
			const strong   = firstDiv?.querySelector("strong");
			const title    = strong?.textContent.trim() || infoTd.textContent.trim();

			let room = "";
			if (firstDiv) {
				const htmlInner  = firstDiv.innerHTML;
				const matchParen = htmlInner.match(/<\/strong>\s*\(([^)]+)\)/);
				if (matchParen) {
					room = matchParen[1].trim();
				} else {
					const badge = firstDiv.querySelector(".room-badge");
					if (badge) room = badge.textContent.trim();
				}
			}

			const langLinks = [...infoTd.querySelectorAll("a.video-link")].map((a) => ({
				label: a.textContent.replace(/\[|\]/g, "").trim(),
				b64:   a.dataset.webUrl    || "",
				type:  a.dataset.videoType || "archive",
			}));

			if (!title) return;
			sessions.push({ title, room, dateDay, timeText, dateDisplay, datetimeISO, langLinks });
		});

		return { sessions, table };
	}

	/* =====================================================================
	   HLS PLAYBACK HELPER
	===================================================================== */

	function playUrl(videoEl, url) {
		if (videoEl._hlsInstance) {
			videoEl._hlsInstance.destroy();
			videoEl._hlsInstance = null;
		}

		const useHLS = /\.m3u8(\?|$)/i.test(url);

		if (useHLS && Hls.isSupported()) {
			const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
			hls.loadSource(url);
			hls.attachMedia(videoEl);
			hls.on(Hls.Events.MANIFEST_PARSED, () => { videoEl.play().catch(() => {}); });
			hls.on(Hls.Events.ERROR, (_ev, data) => {
				if (data.fatal) {
					if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
					else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
					else hls.destroy();
				}
			});
			videoEl._hlsInstance = hls;
		} else if (useHLS && videoEl.canPlayType("application/vnd.apple.mpegurl")) {
			videoEl.src = url;
			videoEl.addEventListener("loadedmetadata", function onM() {
				videoEl.play().catch(() => {});
				videoEl.removeEventListener("loadedmetadata", onM);
			});
		} else {
			videoEl.src = url;
			videoEl.load();
			videoEl.play().catch(() => {});
		}
	}

	/* =====================================================================
	   GLOBAL PLAYBACK COORDINATION
	===================================================================== */

	function notifyPlaybackStart(sourceBlock) {
		document.dispatchEvent(new CustomEvent("wc-playback-start", {
			detail: { source: sourceBlock }
		}));
	}

	/* =====================================================================
	   PROCESS EACH ARCHIVE BLOCK
	===================================================================== */

	document.querySelectorAll(".webcast-archive-block").forEach(async (block) => {
		const baseUrl         = block.dataset.webcastUrl;
		const showSearch      = block.dataset.showSearch === "true";
		const showTabs        = block.dataset.showTabs   === "true";
		const showMenus       = block.dataset.showMenus  === "true";
		const displayMode     = block.dataset.displayMode || "archive";
		const selectedEpisode = block.dataset.selectedEpisode || "";
		const bgImage         = block.dataset.backgroundImage || "";

		if (!baseUrl) return;

		/* =====================================================================
		   SINGLE VIDEO MODE (YouTube-like card)
		===================================================================== */

		if (displayMode === "single") {
			const playerArea  = block.querySelector(".wc-single-player-area");
			const placeholder = block.querySelector(".wc-single-placeholder");
			const videoEl     = block.querySelector(".wc-single-video");
			const langsBox    = block.querySelector(".wc-single-langs");
			const titleEl     = block.querySelector(".wc-single-title");
			const episodeEl   = block.querySelector(".wc-single-episode-label");

			if (!playerArea || !videoEl || !langsBox) return;

			/* ---- Fetch schedule ---- */
			let html = "";
			try {
				const res = await fetch(baseUrl, { mode: "cors" });
				html = await res.text();
			} catch {
				langsBox.innerHTML = `<span class="wc-single-error">Unable to load schedule.</span>`;
				return;
			}

			const { sessions } = parseScheduleHTML(html);

			if (!sessions.length) {
				langsBox.innerHTML = `<span class="wc-single-error">The recording will be available shortly after the session.</span>`;
				return;
			}

			/* ---- Find target episode(s) ---- */
			let targetSessions = sessions;
			if (selectedEpisode) {
				const filtered = sessions.filter((s) => s.title === selectedEpisode);
				if (filtered.length) targetSessions = filtered;
			}

			const isSingleSession = targetSessions.length === 1;
			const mainSession     = targetSessions[0];

			if (isSingleSession && episodeEl) {
				episodeEl.textContent = mainSession.title;
			}

			/* ---- Build language buttons ---- */
			langsBox.innerHTML = "";

			function buildLangBtn(info, session) {
				let decodedUrl = "";
				try { decodedUrl = atob(info.b64); } catch {}

				const btn = document.createElement("button");
				btn.type      = "button";
				btn.className = "wc-single-lang-btn";
				btn.innerHTML = `<span class="wc-single-lang-icon">\u25BA</span>${info.label}`;

				btn.addEventListener("click", () => {
					if (btn.classList.contains("active")) {
						videoEl.pause();
						btn.classList.remove("active");
						btn.querySelector(".wc-single-lang-icon").textContent = "\u25BA";
						return;
					}

					if (!decodedUrl) return;

					const curTime = (!videoEl.paused && videoEl.currentTime > 0)
						? Math.floor(videoEl.currentTime) : 0;

					/* Deactivate all */
					block.querySelectorAll(".wc-single-lang-btn").forEach((b) => {
						b.classList.remove("active");
						const ic = b.querySelector(".wc-single-lang-icon");
						if (ic) ic.textContent = "\u25BA";
					});

					btn.classList.add("active");
					btn.querySelector(".wc-single-lang-icon").textContent = "\u25A0";

					/* Show video, hide placeholder */
					if (placeholder) placeholder.classList.add("wc-hidden");
					videoEl.classList.remove("wc-hidden");

					/* Update episode label for multi-session */
					if (session && episodeEl) episodeEl.textContent = session.title;

					notifyPlaybackStart(block);
					playUrl(videoEl, decodedUrl);

					if (curTime > 0) {
						videoEl.addEventListener("loadedmetadata", function onM() {
							videoEl.currentTime = curTime;
							videoEl.removeEventListener("loadedmetadata", onM);
						});
					}
				});

				return btn;
			}

			if (isSingleSession) {
				mainSession.langLinks.forEach((info) => {
					langsBox.appendChild(buildLangBtn(info, null));
				});
			} else {
				/* Multiple sessions — show grouped */
				targetSessions.forEach((session) => {
					const group = document.createElement("div");
					group.className = "wc-single-lang-group";

					const groupLabel = document.createElement("div");
					groupLabel.className = "wc-single-lang-group-label";
					groupLabel.textContent = session.title + (session.room ? ` (${session.room})` : "");

					const groupDate = document.createElement("span");
					groupDate.className = "wc-single-lang-group-date";
					groupDate.textContent = session.dateDisplay;

					groupLabel.appendChild(groupDate);
					group.appendChild(groupLabel);

					const groupBtns = document.createElement("div");
					groupBtns.className = "wc-single-lang-group-btns";

					session.langLinks.forEach((info) => {
						groupBtns.appendChild(buildLangBtn(info, session));
					});

					group.appendChild(groupBtns);
					langsBox.appendChild(group);
				});
			}

			/* ---- Click placeholder play button ---- */
			const playBtn = block.querySelector(".wc-single-play-btn");
			if (playBtn) {
				playBtn.style.cursor = "pointer";
				playBtn.addEventListener("click", () => {
					const firstBtn = langsBox.querySelector(".wc-single-lang-btn");
					if (firstBtn) firstBtn.click();
				});
			}

			/* ---- Stop if another block starts ---- */
			document.addEventListener("wc-playback-start", (e) => {
				if (e.detail.source !== block) {
					videoEl.pause();
					block.querySelectorAll(".wc-single-lang-btn").forEach((b) => {
						b.classList.remove("active");
						const ic = b.querySelector(".wc-single-lang-icon");
						if (ic) ic.textContent = "\u25BA";
					});
				}
			});

			/* ---- Notify parent accordion ---- */
			const parentAccordionContent = block.closest(".accordion-content");
			if (parentAccordionContent) {
				function refreshAccordionHeight() {
					const parentAccordion = parentAccordionContent.closest(".wp-block-brblocks-accordion");
					if (parentAccordion?.classList.contains("open")) {
						requestAnimationFrame(() => {
							requestAnimationFrame(() => {
								parentAccordionContent.style.maxHeight = parentAccordionContent.scrollHeight + "px";
							});
						});
					}
				}
				const ro = new ResizeObserver(refreshAccordionHeight);
				ro.observe(block);
			}

			return; /* Done — skip archive mode */
		}

		/* =====================================================================
		   GRID MODE (YouTube-style thumbnail grid + lightbox)
		===================================================================== */

		if (displayMode === "grid") {
			const scheduleBox  = block.querySelector(".wc-schedule");
			if (!scheduleBox) return;

			const gridColumns  = block.dataset.gridColumns || "three-col";

			/* ---- Lightbox ---- */
			const lb = document.createElement("div");
			lb.className = "wc-yt-lightbox";
			lb.innerHTML = `
				<div class="wc-yt-lightbox-backdrop"></div>
				<div class="wc-yt-lightbox-dialog">
					<button type="button" class="wc-yt-lightbox-close" aria-label="Close">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="22" height="22">
							<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
						</svg>
					</button>
					<div class="wc-yt-lightbox-player">
						<video class="wc-yt-lb-video" controls playsinline></video>
					</div>
					<div class="wc-yt-lightbox-info">
						<h4 class="wc-yt-lb-title"></h4>
						<div class="wc-yt-lb-langs"></div>
					</div>
				</div>
			`;
			block.appendChild(lb);

			const lbBackdrop = lb.querySelector(".wc-yt-lightbox-backdrop");
			const lbClose    = lb.querySelector(".wc-yt-lightbox-close");
			const lbVideo    = lb.querySelector(".wc-yt-lb-video");
			const lbTitle    = lb.querySelector(".wc-yt-lb-title");
			const lbLangs    = lb.querySelector(".wc-yt-lb-langs");

			function closeLightbox() {
				lb.classList.remove("is-open");
				lbVideo.pause();
				if (lbVideo._hlsInstance) { lbVideo._hlsInstance.destroy(); lbVideo._hlsInstance = null; }
				lbVideo.removeAttribute("src"); lbVideo.load();
			}

			function openLightbox(session, langIdx) {
				lbTitle.textContent = session.title;
				lbLangs.innerHTML = "";

				session.langLinks.forEach((info, idx) => {
					let url = ""; try { url = atob(info.b64); } catch {}
					const btn = document.createElement("button");
					btn.type      = "button";
					btn.className = "wc-yt-lb-lang-btn" + (idx === langIdx ? " active" : "");
					btn.innerHTML = `<span class="wc-yt-lb-lang-icon">${idx === langIdx ? "■" : "►"}</span>${info.label}`;
					btn.addEventListener("click", () => {
						if (!url) return;
						lbLangs.querySelectorAll(".wc-yt-lb-lang-btn").forEach((b, i) => {
							b.classList.toggle("active", i === idx);
							const ic = b.querySelector(".wc-yt-lb-lang-icon");
							if (ic) ic.textContent = i === idx ? "■" : "►";
						});
						notifyPlaybackStart(block);
						playUrl(lbVideo, url);
					});
					lbLangs.appendChild(btn);
				});

				lb.classList.add("is-open");
				notifyPlaybackStart(block);

				const firstInfo = session.langLinks[langIdx];
				if (firstInfo) {
					let url = ""; try { url = atob(firstInfo.b64); } catch {}
					if (url) playUrl(lbVideo, url);
				}
			}

			lbBackdrop.addEventListener("click", closeLightbox);
			lbClose.addEventListener("click", closeLightbox);
			document.addEventListener("keydown", (e) => {
				if (e.key === "Escape" && lb.classList.contains("is-open")) closeLightbox();
			});
			document.addEventListener("wc-playback-start", (e) => {
				if (e.detail.source !== block && lb.classList.contains("is-open")) closeLightbox();
			});

			/* ---- Fetch schedule ---- */
			scheduleBox.innerHTML = `<div class="wc-loading">Loading…</div>`;
			let html = "";
			try {
				const res = await fetch(baseUrl, { mode: "cors" });
				html = await res.text();
			} catch {
				scheduleBox.innerHTML = `<div class="wc-error">Unable to load schedule.</div>`;
				return;
			}

			const { sessions } = parseScheduleHTML(html);
			if (!sessions.length) {
				scheduleBox.innerHTML = `<div class="wc-error">No sessions available.</div>`;
				return;
			}

			let gridSessions = sessions;
			if (selectedEpisode) {
				const ep = sessions.filter((s) => s.title === selectedEpisode);
				if (ep.length) gridSessions = ep;
			}

			/* ---- Build grid ---- */
			scheduleBox.innerHTML = "";
			const ytGrid = document.createElement("div");
			ytGrid.className = `wc-yt-grid wc-yt-grid--${gridColumns}`;

			gridSessions.forEach((session) => {
				const hasLinks = session.langLinks.length > 0;

				const card = document.createElement("div");
				card.className = "wc-yt-card" + (hasLinks ? "" : " wc-yt-card--no-links");

				const thumb = document.createElement("div");
				thumb.className = "wc-yt-card-thumb";
				if (bgImage) thumb.style.backgroundImage = `url(${bgImage})`;

				const playBtn = document.createElement("div");
				playBtn.className = "wc-yt-card-play";
				playBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28"><polygon points="6,3 20,12 6,21"/></svg>`;
				thumb.appendChild(playBtn);

				if (hasLinks) {
					thumb.style.cursor = "pointer";
					thumb.addEventListener("click", () => openLightbox(session, 0));
				}

				const info = document.createElement("div");
				info.className = "wc-yt-card-info";

				const titleEl = document.createElement("h4");
				titleEl.className = "wc-yt-card-title";
				titleEl.textContent = session.title;
				info.appendChild(titleEl);

				if (session.room) {
					const roomEl = document.createElement("span");
					roomEl.className = "wc-yt-card-room";
					roomEl.textContent = session.room;
					info.appendChild(roomEl);
				}

				const dateEl = document.createElement("div");
				dateEl.className = "wc-yt-card-date";
				dateEl.textContent = session.dateDisplay;
				info.appendChild(dateEl);

				card.appendChild(thumb);
				card.appendChild(info);
				ytGrid.appendChild(card);
			});

			scheduleBox.appendChild(ytGrid);
			return;
		}

		/* =====================================================================
		   ARCHIVE MODE (original card grid)
		===================================================================== */

		const scheduleBox = block.querySelector(".wc-schedule");
		if (!scheduleBox) return;

		/* ---- CLOSE A CARD WRAPPER ---- */
		function closeCardInWrapper(w) {
			const exp  = w.querySelector(".wc-card-player");
			const comp = w.querySelector(".wc-card-compact");
			const vid  = w.querySelector(".wc-card-video");
			if (exp) {
				exp.classList.remove("wc-card-player-visible");
				exp.classList.add("wc-card-player-hidden");
			}
			if (comp) comp.classList.remove("wc-card-compact-hidden");
			if (vid) {
				vid.pause();
				if (vid._hlsInstance) { vid._hlsInstance.destroy(); vid._hlsInstance = null; }
				vid.src = ""; vid.load();
			}
			w.querySelectorAll(".wc-card-lang-btn, .wc-card-plang-btn").forEach((b) => {
				b.classList.remove("active");
				const ic = b.querySelector(".wc-card-lang-icon") || b.querySelector(".wc-card-plang-icon");
				if (ic) ic.textContent = "\u25BA";
			});
		}

		document.addEventListener("wc-playback-start", (e) => {
			if (e.detail.source !== block) {
				block.querySelectorAll(".wc-card-wrapper").forEach((w) => closeCardInWrapper(w));
			}
		});

		document.addEventListener("keydown", (e) => {
			if (
				e.key === "Backspace" &&
				!["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)
			) {
				e.preventDefault();
				block.querySelectorAll(".wc-card-wrapper").forEach((w) => closeCardInWrapper(w));
			}
		});

		/* ---- LOAD SCHEDULE ---- */
		scheduleBox.innerHTML = `<div class="wc-loading">Loading schedule\u2026</div>`;

		let html = "";
		try {
			const res = await fetch(baseUrl, { mode: "cors" });
			html = await res.text();
		} catch {
			scheduleBox.innerHTML = `<div class="wc-error">Unable to load schedule.</div>`;
			return;
		}

		const { sessions, table } = parseScheduleHTML(html);

		if (!sessions.length) {
			if (table) {
				scheduleBox.innerHTML = "";
				const cloned = table.cloneNode(true);
				cloned.querySelectorAll("a.video-link").forEach((a) => {
					const b64 = a.dataset.webUrl;
					if (!b64) return;
					let url = "";
					try { url = atob(b64); } catch { return; }
					a.removeAttribute("href");
					a.dataset.streamUrl = url;
					a.classList.add("wc-lang-choice");
					a.addEventListener("click", (e) => {
						e.preventDefault();
						if (url) window.open(url, "_blank");
					});
				});
				scheduleBox.appendChild(cloned);
			} else {
				scheduleBox.innerHTML = `<div class="wc-error">The recording will be available shortly after the session.</div>`;
			}
			return;
		}

		/* ---- EPISODE FILTER ---- */
		let filteredSessions = sessions;
		if (selectedEpisode) {
			const ep = sessions.filter((s) => s.title === selectedEpisode);
			if (ep.length) filteredSessions = ep;
		}

		/* ---- FILTERS ---- */
		const uniqueDates    = [...new Set(filteredSessions.map((s) => s.dateDay))];
		const uniqueRooms    = [...new Set(filteredSessions.map((s) => s.room).filter(Boolean))];
		const uniqueSessions = [...new Set(filteredSessions.flatMap((s) => extractAllSessions(s.title)))];

		const filterState = { search: "", date: "", room: "", session: "" };

		const filtersWrapper = document.createElement("div");
		filtersWrapper.className = "wc-filters";

		const grid = document.createElement("div");
		grid.className = "wc-grid";

		/* SEARCH */
		const searchWrap = document.createElement("div");
		searchWrap.className = "wc-search-wrapper";
		const searchIcon = document.createElement("span");
		searchIcon.className = "wc-search-icon";
		searchIcon.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" stroke="#999" stroke-width="2" fill="none"/><line x1="16" y1="16" x2="22" y2="22" stroke="#999" stroke-width="2"/></svg>`;
		const searchInput = document.createElement("input");
		searchInput.type = "search"; searchInput.placeholder = "Search sessions\u2026"; searchInput.className = "wc-search-input";
		searchInput.addEventListener("input", () => { filterState.search = searchInput.value.toLowerCase(); renderCards(); });
		searchWrap.appendChild(searchIcon); searchWrap.appendChild(searchInput);

		/* DAY TABS */
		const daysWrap = document.createElement("div");
		daysWrap.className = "wc-days-tabs";
		const allBtn = document.createElement("button");
		allBtn.type = "button"; allBtn.className = "wc-day-tab active"; allBtn.textContent = "All days";
		allBtn.addEventListener("click", () => { filterState.date = ""; daysWrap.querySelectorAll(".wc-day-tab").forEach((b) => b.classList.remove("active")); allBtn.classList.add("active"); renderCards(); });
		daysWrap.appendChild(allBtn);

		uniqueDates.forEach((d, i) => {
			const btn = document.createElement("button");
			btn.type = "button"; btn.className = "wc-day-tab"; btn.textContent = `${formatDateLabel(d)} (Day ${i + 1})`; btn.dataset.date = d;
			btn.addEventListener("click", () => { filterState.date = d; daysWrap.querySelectorAll(".wc-day-tab").forEach((b) => b.classList.remove("active")); btn.classList.add("active"); renderCards(); });
			daysWrap.appendChild(btn);
		});

		/* MOBILE DAY SELECT */
		const daysSelectWrap = document.createElement("div"); daysSelectWrap.className = "wc-days-select-wrap";
		const daysSelect = document.createElement("select"); daysSelect.className = "wc-days-select";
		const optAllDays = document.createElement("option"); optAllDays.value = ""; optAllDays.textContent = "All days"; daysSelect.appendChild(optAllDays);
		uniqueDates.forEach((d, index) => { const opt = document.createElement("option"); opt.value = d; opt.textContent = `${formatDateLabel(d)} (Day ${index + 1})`; daysSelect.appendChild(opt); });
		daysSelect.addEventListener("change", () => {
			filterState.date = daysSelect.value;
			daysWrap.querySelectorAll(".wc-day-tab").forEach((t) => t.classList.remove("active"));
			if (daysSelect.value === "") { allBtn.classList.add("active"); }
			else { const tab = [...daysWrap.querySelectorAll(".wc-day-tab")].find((t) => t.dataset.date === daysSelect.value); if (tab) tab.classList.add("active"); }
			renderCards();
		});
		daysSelectWrap.appendChild(daysSelect);

		/* ROOM MENU */
		let roomWrap = null;
		if (uniqueRooms.length > 0) {
			roomWrap = document.createElement("div"); roomWrap.className = "wc-filter-room-inline";
			const selectRoom = document.createElement("select");
			const optAllRoom = document.createElement("option"); optAllRoom.value = ""; optAllRoom.textContent = "All rooms"; selectRoom.appendChild(optAllRoom);
			uniqueRooms.forEach((r) => { const o = document.createElement("option"); o.value = r; o.textContent = r; selectRoom.appendChild(o); });
			selectRoom.addEventListener("change", () => { filterState.room = selectRoom.value; renderCards(); });
			roomWrap.appendChild(selectRoom);
		}

		/* SESSION MENU */
		let sessionWrap = null;
		if (uniqueSessions.length > 0) {
			sessionWrap = document.createElement("div"); sessionWrap.className = "wc-filter-session-inline";
			const selectSess = document.createElement("select");
			const optAllSess = document.createElement("option"); optAllSess.value = ""; optAllSess.textContent = "All sessions"; selectSess.appendChild(optAllSess);
			uniqueSessions.forEach((label) => { const o = document.createElement("option"); o.value = label; o.textContent = label; selectSess.appendChild(o); });
			selectSess.addEventListener("change", () => { filterState.session = selectSess.value; renderCards(); });
			sessionWrap.appendChild(selectSess);
		}

		/* LAYOUT */
		const isMobile = window.matchMedia("(max-width: 768px)").matches;
		if (showSearch) filtersWrapper.appendChild(searchWrap);
		if (!isMobile) {
			if (showTabs) filtersWrapper.appendChild(daysWrap);
			if (showMenus) {
				if (showTabs) { if (roomWrap) daysWrap.appendChild(roomWrap); if (sessionWrap) daysWrap.appendChild(sessionWrap); }
				else { if (roomWrap) filtersWrapper.appendChild(roomWrap); if (sessionWrap) filtersWrapper.appendChild(sessionWrap); }
			}
		} else {
			daysWrap.style.display = "none";
			const mobileRow = document.createElement("div"); mobileRow.className = "wc-mobile-filters";
			if (showTabs) mobileRow.appendChild(daysSelectWrap);
			if (showMenus) { if (roomWrap) mobileRow.appendChild(roomWrap); if (sessionWrap) mobileRow.appendChild(sessionWrap); }
			filtersWrapper.appendChild(mobileRow);
		}

		/* ---- RENDER CARDS ---- */
		function renderCards() {
			grid.innerHTML = "";

			const filtered = filteredSessions.filter((s) => {
				if (filterState.search && !filterState.session) { if (!s.title.toLowerCase().includes(filterState.search)) return false; }
				if (filterState.date && s.dateDay !== filterState.date) return false;
				if (filterState.room && s.room !== filterState.room) return false;
				if (filterState.session) { const found = extractAllSessions(s.title); if (!found.includes(filterState.session)) return false; }
				return true;
			});

			if (!filtered.length) {
				const empty = document.createElement("div"); empty.className = "wc-empty"; empty.textContent = "No sessions match your filters.";
				grid.appendChild(empty); return;
			}

			filtered.forEach((session) => {
				const wrapper = document.createElement("div"); wrapper.className = "wc-card-wrapper";

				/* COMPACT */
				const compact = document.createElement("div"); compact.className = "wc-card-compact";
				let thumbBgStyle = "";
				if (bgImage) { thumbBgStyle = ` style="background-image: url(${bgImage}); background-size: cover; background-position: center;"`; }

				compact.innerHTML = `
					<div class="wc-card-thumb"${thumbBgStyle}>
						<span class="wc-card-thumb-wm">ITU</span>
						<div class="wc-card-thumb-play"><svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><polygon points="6,3 20,12 6,21"/></svg></div>
					</div>
					<div class="wc-card-info">
						<h4 class="wc-card-title">${session.title}</h4>
						${session.room ? '<span class="wc-card-room">' + session.room + '</span>' : ''}
						<div class="wc-card-langs"></div>
						<div class="wc-card-date">${session.dateDisplay}</div>
					</div>
				`;

				/* EXPANDED */
				const expanded = document.createElement("div"); expanded.className = "wc-card-player wc-card-player-hidden";
				expanded.innerHTML = `
					<div class="wc-card-player-grid">
						<div class="wc-card-player-video-col"><video class="wc-card-video" controls playsinline></video></div>
						<div class="wc-card-player-sidebar">
							<button type="button" class="wc-card-player-close" aria-label="Close"><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
							<h4 class="wc-card-player-title">${session.title}</h4>
							${session.room ? '<span class="wc-card-player-room">' + session.room + '</span>' : ''}
							<span class="wc-card-player-date">${session.dateDisplay}</span>
							<div class="wc-card-player-lang-label">Choose language</div>
							<div class="wc-card-player-langs"></div>
						</div>
					</div>
				`;

				const langBox = compact.querySelector(".wc-card-langs");
				const playerLangBox = expanded.querySelector(".wc-card-player-langs");
				const cardVideo = expanded.querySelector(".wc-card-video");
				const closeExpBtn = expanded.querySelector(".wc-card-player-close");
				const thumbPlay = compact.querySelector(".wc-card-thumb-play");

				function closeCard() { closeCardInWrapper(wrapper); }
				if (closeExpBtn) closeExpBtn.addEventListener("click", closeCard);

				function playInCard(url) {
					notifyPlaybackStart(block);
					grid.querySelectorAll(".wc-card-wrapper").forEach((w) => { if (w !== wrapper) closeCardInWrapper(w); });
					compact.classList.add("wc-card-compact-hidden");
					expanded.classList.remove("wc-card-player-hidden");
					expanded.classList.add("wc-card-player-visible");

					const cleanNew = url.split("?")[0].split("#")[0];
					const cleanCurrent = (cardVideo.src || "").split("?")[0].split("#")[0];
					if (cleanNew === cleanCurrent) { cardVideo.play(); expanded.scrollIntoView({ behavior: "smooth", block: "center" }); return; }

					playUrl(cardVideo, url);
					expanded.scrollIntoView({ behavior: "smooth", block: "center" });
				}

				function syncLangs(activeIdx) {
					wrapper.querySelectorAll(".wc-card-lang-btn").forEach((b, i) => { b.classList.toggle("active", i === activeIdx); const ic = b.querySelector(".wc-card-lang-icon"); if (ic) ic.textContent = i === activeIdx ? "\u25A0" : "\u25BA"; });
					wrapper.querySelectorAll(".wc-card-plang-btn").forEach((b, i) => { b.classList.toggle("active", i === activeIdx); const ic = b.querySelector(".wc-card-plang-icon"); if (ic) ic.textContent = i === activeIdx ? "\u25A0" : "\u25BA"; });
				}

				session.langLinks.forEach((info, langIdx) => {
					let decodedUrl = ""; try { decodedUrl = atob(info.b64); } catch {}

					const tag = document.createElement("span"); tag.className = "wc-card-lang-btn";
					tag.innerHTML = '<span class="wc-card-lang-icon">\u25BA</span>' + info.label;
					tag.addEventListener("click", () => { if (tag.classList.contains("active")) { closeCard(); return; } if (!decodedUrl) return; syncLangs(langIdx); playInCard(decodedUrl); });
					langBox.appendChild(tag);

					const ptag = document.createElement("span"); ptag.className = "wc-card-plang-btn";
					ptag.innerHTML = '<span class="wc-card-plang-icon">\u25BA</span>' + info.label;
					ptag.addEventListener("click", () => {
						if (ptag.classList.contains("active")) { closeCard(); return; } if (!decodedUrl) return;
						const curTime = (!cardVideo.paused && cardVideo.currentTime > 0) ? Math.floor(cardVideo.currentTime) : 0;
						syncLangs(langIdx);
						if (cardVideo._hlsInstance) { cardVideo._hlsInstance.destroy(); cardVideo._hlsInstance = null; }
						cardVideo.removeAttribute("src"); cardVideo.load();
						setTimeout(() => { playInCard(decodedUrl); if (curTime > 0) { cardVideo.addEventListener("loadedmetadata", function onM() { cardVideo.currentTime = curTime; cardVideo.removeEventListener("loadedmetadata", onM); }); } }, 50);
					});
					playerLangBox.appendChild(ptag);
				});

				if (thumbPlay) { thumbPlay.addEventListener("click", () => { const firstBtn = langBox.querySelector(".wc-card-lang-btn"); if (firstBtn) firstBtn.click(); }); }

				wrapper.appendChild(compact);
				wrapper.appendChild(expanded);
				grid.appendChild(wrapper);
			});
		}

		/* MOUNT */
		scheduleBox.innerHTML = "";
		scheduleBox.appendChild(filtersWrapper);
		scheduleBox.appendChild(grid);
		renderCards();

		/* ACCORDION */
		const parentAccordionContent = block.closest(".accordion-content");
		if (parentAccordionContent) {
			function refreshAccordionHeight() {
				const parentAccordion = parentAccordionContent.closest(".wp-block-brblocks-accordion");
				if (parentAccordion?.classList.contains("open")) {
					requestAnimationFrame(() => { requestAnimationFrame(() => { parentAccordionContent.style.maxHeight = parentAccordionContent.scrollHeight + "px"; }); });
				}
			}
			const ro = new ResizeObserver(refreshAccordionHeight);
			ro.observe(block);
		}
	});
});